import React, { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import PageHeader from "../../components/common/PageHeader";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import {
  AccountingCategory,
  CategoryType,
  FreeAgentCodeResolution,
  FreeAgentUnknownCode,
  FreeAgentValidationResult,
  StatementSection,
} from "@akfdev-com/shared/src/models";
import {
  buildingsApi,
  categoriesApi,
  freeagentImportApi,
} from "../../services/api";

interface SheetInfo {
  name: string;
  rows: number;
  columns: number;
}

interface Building {
  id: string;
  name: string;
}

interface ImportResult {
  message: string;
  summary: {
    accountingCategories: number;
    bankAccounts: number;
    bankTransactions: number;
    contacts: number;
    financialRecords: number;
    invoicesImported: number;
    billsImported: number;
    journalEntries: number;
    errors: string[];
  };
  validation?: FreeAgentValidationResult;
}

interface ResolutionDraft {
  name: string;
  categoryType: CategoryType;
  statementSection: StatementSection;
  parentCode?: string;
}

// ── Contact preview types (P8 T2.2) ─────────────────────────────────────────
type OverrideAction = "accept" | "assign-unit" | "ignore";

interface ClassifiedShape {
  role: "resident" | "supplier" | "customer";
  unitId?: string;
  matchType?: "exact-unit" | "building-partial";
  buildingId?: string;
  sharedPostcode?: string;
  sharedTokens?: string[];
  unresolvedUnit?: boolean;
}

interface PreviewContact {
  key: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  classified: ClassifiedShape;
  invoiceCount: number;
}

interface PreviewUnit {
  id: string;
  name: string;
}

interface ContactPreview {
  building: { id: string; name: string } | null;
  units: PreviewUnit[];
  contacts: PreviewContact[];
}

// ── First-import setup analyser types (Bucket D3/D4) ────────────────────────
// Mirrored from backend `analyse/firstImport.ts` — kept as a local mirror to
// avoid coupling the frontend bundle to a backend Lambda folder. The shapes
// are stable: any additive change on the backend requires a mirrored update
// here too. Requirement 10.6–10.10 in the spec.

interface ManagementStartDateGap {
  kind: "management-start-date";
  suggestedDate: string;
  evidence: {
    sourceInvoiceRef: string;
    sourceInvoiceDate: string;
    contactName: string;
  };
}

interface ServiceChargePlanGap {
  kind: "service-charge-plan";
  suggestedStartDate: string;
  suggestedMonthlyAmount: number;
  frequency: "monthly";
  evidence: {
    monthLabel: string;
    invoiceCount: number;
    perContact: Array<{ contactName: string; amountPence: number }>;
  };
  activePlansCount: number;
}

interface UnitSharesGap {
  kind: "unit-shares";
  suggestions: Array<{
    unitId: string;
    unitName: string;
    contactName: string;
    numerator: number;
    denominator: number;
    monthOneAmountPence: number;
  }>;
}

interface RetroReclassifyGap {
  kind: "retro-reclassify";
  candidates: Array<{
    recordId: string;
    contactName: string;
    unitId: string;
    date: string;
    amountPence: number;
    description: string;
  }>;
}

type SetupGap =
  | ManagementStartDateGap
  | ServiceChargePlanGap
  | UnitSharesGap
  | RetroReclassifyGap;

interface SanityWarning {
  kind:
    | "shares-do-not-sum-to-one"
    | "resident-count-vs-unit-count"
    | "plan-share-mismatch";
  message: string;
}

interface SetupPlan {
  buildingId: string;
  gaps: SetupGap[];
  sanityWarnings: SanityWarning[];
}

interface ApplySetupResult {
  message: string;
  /**
   * Populated only on a successful apply (HTTP 200). On any error
   * response the body is `{ message: string }` alone — the render
   * MUST NOT dereference `applied` without a truthiness check first.
   */
  applied?: {
    managementStartDate?: string;
    planId?: string;
    unitShareUpdates: number;
    retroReclassifyDeferred: number;
  };
}

/** Format integer pence as £ with 2dp (Rule 1, design language). */
const formatPence = (pence: number | undefined | null): string => {
  const n = typeof pence === "number" && !Number.isNaN(pence) ? pence : 0;
  const gbp = n / 100;
  const sign = gbp < 0 ? "-" : "";
  const absStr = Math.abs(gbp).toFixed(2);
  return `${sign}£${absStr}`;
};

const FreeAgentImport: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [sheets, setSheets] = useState<SheetInfo[]>([]);
  const [sheetData, setSheetData] = useState<Record<string, string>>({});
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [parentGroups, setParentGroups] = useState<AccountingCategory[]>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<string>("");
  const [importing, setImporting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [resolvingCode, setResolvingCode] = useState<string | null>(null);
  const [validation, setValidation] =
    useState<FreeAgentValidationResult | null>(null);
  const [resolutionDrafts, setResolutionDrafts] = useState<
    Record<string, ResolutionDraft>
  >({});
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  // Contact preview / review — added for P8 T2.2 (per-contact classification
  // review before Confirm). `previewLoading` gates the review UI while the
  // server classifies against the selected building.
  const [previewLoading, setPreviewLoading] = useState(false);
  const [contactPreview, setContactPreview] = useState<ContactPreview | null>(
    null,
  );
  const [overrides, setOverrides] = useState<
    Record<string, { action: OverrideAction; unitId?: string }>
  >({});

  // First-import setup wizard (Bucket D3/D4). `setupPlan` is populated
  // after runAnalyse() succeeds; `acceptedGaps` is a bit-flag map keyed by
  // gap kind toggling whether each gap is applied on Apply Setup. The
  // wizard is inline (below Preview Contacts) — a mid-flow modal would
  // break the existing UX rhythm.
  const [analysing, setAnalysing] = useState(false);
  const [setupPlan, setSetupPlan] = useState<SetupPlan | null>(null);
  const [acceptedGaps, setAcceptedGaps] = useState<Record<string, boolean>>({});
  const [applyingSetup, setApplyingSetup] = useState(false);
  const [applySetupResult, setApplySetupResult] =
    useState<ApplySetupResult | null>(null);
  const [setupSkipped, setSetupSkipped] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load buildings for optional assignment
  useEffect(() => {
    const loadData = async () => {
      try {
        const [buildingData, categoryData] = await Promise.all([
          buildingsApi.getAll(),
          categoriesApi.getAll(),
        ]);
        setBuildings(Array.isArray(buildingData) ? buildingData : []);
        const categories = Array.isArray(categoryData)
          ? (categoryData as AccountingCategory[])
          : [];
        setParentGroups(categories.filter((cat) => !cat.parentCode));
      } catch (err) {
        console.error("Error loading import dependencies:", err);
      }
    };
    loadData();
  }, []);

  const processFile = (selectedFile: File) => {
    setFile(selectedFile);
    setSheets([]);
    setSheetData({});
    setResult(null);
    setError(null);
    setParseError(null);
    setValidation(null);
    setResolutionDrafts({});

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) throw new Error("Failed to read file");

        const workbook = XLSX.read(data, { type: "array" });
        const sheetsInfo: SheetInfo[] = [];
        const csvData: Record<string, string> = {};

        for (const sheetName of workbook.SheetNames) {
          const worksheet = workbook.Sheets[sheetName];
          const csv = XLSX.utils.sheet_to_csv(worksheet);
          const rows = csv.split("\n").filter((line) => line.trim()).length - 1; // exclude header
          const headerLine = csv.split("\n")[0] || "";
          const columns = headerLine.split(",").length;

          sheetsInfo.push({
            name: sheetName,
            rows: Math.max(0, rows),
            columns,
          });
          csvData[sheetName] = csv;
        }

        setSheets(sheetsInfo);
        setSheetData(csvData);
      } catch (err) {
        console.error("Error parsing XLSX:", err);
        setParseError(
          "Failed to parse the Excel file. Is it a valid FreeAgent export?",
        );
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const defaultParentForType = (type: CategoryType): string | undefined => {
    if (type === "income") return "1000";
    if (type === "asset") return "4000";
    if (type === "liability") return "5000";
    if (type === "equity") return "6000";
    return "2000";
  };

  const buildDefaultDraft = (
    unknownCode: FreeAgentUnknownCode,
  ): ResolutionDraft => ({
    name:
      unknownCode.sample.description || `Category ${unknownCode.internalCode}`,
    categoryType: "expense",
    statementSection: "profit-loss",
    parentCode: "2000",
  });

  const runValidation = async () => {
    if (Object.keys(sheetData).length === 0) return;
    setValidating(true);
    setError(null);
    try {
      const response: FreeAgentValidationResult =
        await freeagentImportApi.validate({
          buildingId: selectedBuilding || undefined,
          data: sheetData,
        });
      setValidation(response);
      if (response.unknownCodes.length > 0) {
        setResolutionDrafts((prev) => {
          const next = { ...prev };
          for (const code of response.unknownCodes) {
            if (!next[code.internalCode]) {
              next[code.internalCode] = buildDefaultDraft(code);
            }
          }
          return next;
        });
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Validation failed. Please try again.",
      );
    } finally {
      setValidating(false);
    }
  };

  const runPreview = async () => {
    if (Object.keys(sheetData).length === 0 || !selectedBuilding) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const response: ContactPreview = await freeagentImportApi.preview({
        buildingId: selectedBuilding,
        data: sheetData,
      });
      setContactPreview(response);
      // Seed the overrides map with `accept` for every contact so the review
      // UI has a stable per-row selection state without needing to backfill
      // on every render.
      const seed: Record<string, { action: OverrideAction; unitId?: string }> =
        {};
      for (const c of response.contacts) {
        seed[c.key] = { action: "accept" };
      }
      setOverrides(seed);
      // Clear any prior wizard state — the treasurer may have changed the
      // sheet or the overrides, and we want a fresh analyse pass.
      setSetupPlan(null);
      setAcceptedGaps({});
      setApplySetupResult(null);
      setSetupSkipped(false);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Preview failed. Please check the building selection and try again.",
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  // Bucket D3 — first-import setup analyser. Sends the parsed sheets plus
  // the treasurer's per-contact overrides to the server, which derives a
  // SetupPlan (management start date + service-charge plan + per-unit
  // shares + retro-reclassify candidates). No writes; render the plan
  // inline below the Preview Contacts panel for the treasurer to accept.
  const runAnalyse = async () => {
    if (!selectedBuilding || Object.keys(sheetData).length === 0) return;
    // Overrides may be empty on first run — the pure-classification path
    // still produces useful gaps. Send whatever the treasurer picked.
    const trimmedOverrides = contactPreview
      ? Object.fromEntries(
          Object.entries(overrides).filter(([, v]) => v.action !== "accept"),
        )
      : {};
    setAnalysing(true);
    setError(null);
    try {
      const response: SetupPlan = await freeagentImportApi.analyse({
        buildingId: selectedBuilding,
        data: sheetData,
        overrides: { contacts: trimmedOverrides },
      });
      setSetupPlan(response);
      // Seed acceptedGaps to true for every gap except retro-reclassify
      // (which is deferred to Bucket D5 and cannot be applied yet).
      const seedAccepted: Record<string, boolean> = {};
      for (const gap of response.gaps ?? []) {
        seedAccepted[gap.kind] = gap.kind !== "retro-reclassify";
      }
      setAcceptedGaps(seedAccepted);
      setApplySetupResult(null);
      setSetupSkipped(false);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "First-import analysis failed. Please try again or Skip setup.",
      );
    } finally {
      setAnalysing(false);
    }
  };

  const toggleAcceptGap = (kind: string) => {
    setAcceptedGaps((prev) => ({ ...prev, [kind]: !prev[kind] }));
  };

  // Bucket D4 — atomically apply the accepted gaps. Retro-reclassify is
  // filtered out client-side too (defence in depth); the server also
  // skips it and reports it under `applied.retroReclassifyDeferred`.
  //
  // The `applySetup` client accepts 200 | 400 | 500 as valid responses.
  // A 500 comes back as `{ message: "apply-setup failed: <reason>" }`
  // with NO `applied` payload — the render block guards against this
  // shape (see below) rather than dereferencing `.applied` and
  // crashing the whole page with a runtime TypeError (the "blank
  // screen after Apply setup" bug reported 2026-08-23). We ALSO push
  // the message into the top-of-form ServerAlert so a treasurer
  // scrolling from the bottom of the wizard sees the failure.
  const runApplySetup = async () => {
    if (!setupPlan || !selectedBuilding) return;
    const toApply = setupPlan.gaps.filter(
      (g) => acceptedGaps[g.kind] && g.kind !== "retro-reclassify",
    );
    if (toApply.length === 0) {
      setError(
        "No gaps selected for apply. Tick at least one gap or Skip setup.",
      );
      return;
    }
    setApplyingSetup(true);
    setError(null);
    try {
      const response: ApplySetupResult = await freeagentImportApi.applySetup({
        buildingId: selectedBuilding,
        accepted: {
          gaps: toApply as unknown as Array<Record<string, unknown>>,
        },
      });
      setApplySetupResult(response);
      // Surface the failure at the top of the form too when the server
      // reported an error shape — the response body has no `applied`
      // when the Lambda's try/catch returned 400/500.
      if (!response.applied) {
        setError(
          response.message ??
            "Apply setup failed with no error message. Check the Lambda logs.",
        );
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Apply setup failed. The building state is unchanged; you can retry or Skip setup.",
      );
    } finally {
      setApplyingSetup(false);
    }
  };

  /** Skip the setup wizard entirely so the treasurer can Confirm Import. */
  const skipSetup = () => {
    setSetupSkipped(true);
    setError(null);
  };

  /**
   * Has the wizard been resolved (applied cleanly, skipped, or empty)?
   *
   * A partial-error state — server returned but no `applied` payload —
   * DOES NOT count as ready: the treasurer must either Retry, Re-analyse,
   * or Skip setup explicitly. Otherwise a silent 500 would let Confirm
   * Import proceed against a half-configured building.
   */
  const setupReady =
    setupSkipped ||
    (applySetupResult !== null && !!applySetupResult.applied) ||
    (setupPlan !== null && setupPlan.gaps.length === 0);

  const setContactAction = (
    key: string,
    action: OverrideAction,
    unitId?: string,
  ) => {
    setOverrides((prev) => ({
      ...prev,
      [key]: { action, unitId },
    }));
  };

  const overrideValidationError = (): string | null => {
    if (!contactPreview) return null;
    for (const c of contactPreview.contacts) {
      const o = overrides[c.key];
      if (!o) continue;
      if (o.action === "ignore" && c.invoiceCount > 0) {
        return `Cannot ignore "${c.name}" — they have ${c.invoiceCount} invoice(s) in this import. Choose Accept or Match to Unit instead.`;
      }
      if (o.action === "assign-unit" && !o.unitId) {
        return `Please pick a unit for "${c.name}" or change the action.`;
      }
    }
    return null;
  };

  const handleDraftChange = (
    internalCode: string,
    field: keyof ResolutionDraft,
    value: string,
  ) => {
    setResolutionDrafts((prev) => {
      const current = prev[internalCode] || {
        name: "",
        categoryType: "expense" as CategoryType,
        statementSection: "profit-loss" as StatementSection,
        parentCode: "2000",
      };
      const nextDraft: ResolutionDraft = {
        ...current,
        [field]: value,
      } as ResolutionDraft;

      if (field === "categoryType") {
        nextDraft.parentCode = defaultParentForType(value as CategoryType);
        if (value === "income" || value === "expense") {
          nextDraft.statementSection = "profit-loss";
        } else {
          nextDraft.statementSection = "balance-sheet";
        }
      }

      return {
        ...prev,
        [internalCode]: nextDraft,
      };
    });
  };

  const handleResolveCode = async (unknownCode: FreeAgentUnknownCode) => {
    const draft = resolutionDrafts[unknownCode.internalCode];
    if (!draft || !draft.name.trim()) {
      setError(`Please provide a category name for code ${unknownCode.code}`);
      return;
    }

    const payload: FreeAgentCodeResolution = {
      code: unknownCode.code,
      name: draft.name.trim(),
      categoryType: draft.categoryType,
      statementSection: draft.statementSection,
      parentCode: draft.parentCode,
    };

    setResolvingCode(unknownCode.internalCode);
    setError(null);
    try {
      const response = await freeagentImportApi.resolve({
        buildingId: selectedBuilding || undefined,
        data: sheetData,
        resolutions: [payload],
      });
      if (response.validation) {
        setValidation(response.validation as FreeAgentValidationResult);
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          `Failed to resolve code ${unknownCode.code}`,
      );
    } finally {
      setResolvingCode(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) processFile(selectedFile);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile && droppedFile.name.endsWith(".xlsx")) {
      processFile(droppedFile);
    } else {
      setParseError("Please drop a .xlsx file");
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleImport = async () => {
    if (Object.keys(sheetData).length === 0) return;

    if (!validation) {
      await runValidation();
      return;
    }

    if (!validation.readyToImport) {
      setError(
        "Import is blocked until all unknown nominal codes are resolved.",
      );
      return;
    }

    // Guard: when the user selected a building, they must have reviewed the
    // per-contact classification via the preview step so we can honour their
    // accept/assign-unit/ignore decisions on confirm.
    if (selectedBuilding && !contactPreview) {
      setError('Please review the "Preview Contacts" step before importing.');
      return;
    }

    // Client-side pre-flight for the ignore-with-invoices rule. The backend
    // enforces it too, but showing the error inline avoids a round-trip.
    const validationError = overrideValidationError();
    if (validationError) {
      setError(validationError);
      return;
    }

    setImporting(true);
    setError(null);
    setResult(null);

    try {
      // Only send overrides for contacts whose action isn't the default
      // "accept" — this keeps the request lean and lets the server treat
      // "no override" identically to "accept".
      const trimmedOverrides = contactPreview
        ? Object.fromEntries(
            Object.entries(overrides).filter(([, v]) => v.action !== "accept"),
          )
        : undefined;

      const response = await freeagentImportApi.import({
        buildingId: selectedBuilding || undefined,
        data: sheetData,
        overrides: trimmedOverrides
          ? { contacts: trimmedOverrides }
          : undefined,
      });
      if (response.validation && !response.validation.readyToImport) {
        setValidation(response.validation);
        setError("Import blocked: resolve unknown nominal codes first.");
        return;
      }
      if (response.errors && Array.isArray(response.errors)) {
        // 400 from the overrides validation carries an errors[] payload —
        // surface the first offender to the treasurer.
        const first = response.errors[0];
        if (first?.reason) {
          setError(first.reason);
          return;
        }
      }
      setResult(response);
    } catch (err: any) {
      console.error("Import error:", err);
      setError(
        err.response?.data?.message || "Import failed. Please try again.",
      );
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setSheets([]);
    setSheetData({});
    setResult(null);
    setError(null);
    setParseError(null);
    setValidation(null);
    setResolutionDrafts({});
    setContactPreview(null);
    setOverrides({});
    setSetupPlan(null);
    setAcceptedGaps({});
    setApplySetupResult(null);
    setSetupSkipped(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const totalRows = sheets.reduce((sum, s) => sum + s.rows, 0);
  const unresolvedCodes = validation?.unknownCodes || [];
  const importBlocked = !!validation && !validation.readyToImport;

  const knownSheets = [
    "Bank Accounts",
    "Bank Account Entries",
    "Bank Account Transactions",
    "Contacts",
    "Invoices",
    "Bills",
    "Journals",
    "Opening Balances",
    "Company",
    "Users",
  ];

  return (
    <div className="">
      <PageHeader
        title="FreeAgent Import"
        description="Import data from a FreeAgent company export (.xlsx)"
      />

      {/* File upload zone */}
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="mt-6 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg bg-white cursor-pointer hover:border-blue-400 transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="space-y-1 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="flex text-sm text-gray-600">
              <span className="font-medium text-primary-600 hover:text-primary-500">
                Click to upload
              </span>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-xs text-gray-500">
              FreeAgent company export (.xlsx)
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      )}

      {parseError && (
        <div className="mt-4">
          <ErrorAlert message={parseError} />
        </div>
      )}

      {/* File loaded — show preview */}
      {file && sheets.length > 0 && (
        <>
          {/* File info bar */}
          <div className="mt-6 bg-white shadow rounded-lg p-4 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">{file.name}</h3>
              <p className="text-xs text-gray-500">
                {sheets.length} sheets &middot; {totalRows.toLocaleString()}{" "}
                data rows &middot; {(file.size / 1024).toFixed(0)} KB
              </p>
            </div>
            <button
              onClick={handleReset}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Change file
            </button>
          </div>

          {/* Sheet preview */}
          <div className="mt-4 bg-white shadow overflow-hidden rounded-lg">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-700">
                Detected Sheets
              </h3>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sheet Name
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rows
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Columns
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sheets.map((sheet) => {
                  const isKnown = knownSheets.includes(sheet.name);
                  return (
                    <tr key={sheet.name}>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                        {sheet.name}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500 text-right">
                        {sheet.rows.toLocaleString()}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500 text-right">
                        {sheet.columns}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm">
                        {isKnown ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Supported
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            Skipped
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Building assignment */}
          <div className="mt-4 bg-white shadow rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assign to Building (optional)
            </label>
            <select
              value={selectedBuilding}
              onChange={(e) => {
                setSelectedBuilding(e.target.value);
                // Building change invalidates any prior classification.
                setContactPreview(null);
                setOverrides({});
                setSetupPlan(null);
                setAcceptedGaps({});
                setApplySetupResult(null);
                setSetupSkipped(false);
              }}
              className="block w-full sm:w-80 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              <option value="">Company Level (no building)</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Leave empty to import as company-level data. Select a building to
              classify contacts as residents (and get the Preview Contacts step
              to review or reassign them).
            </p>
          </div>

          {/* Validation, Preview, and Import actions */}
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <button
              onClick={runValidation}
              disabled={validating || importing}
              className="inline-flex items-center px-6 py-3 border border-primary-200 text-sm font-medium rounded-md shadow-sm text-primary-700 bg-white hover:bg-primary-50 disabled:opacity-50"
            >
              {validating ? "Validating..." : "Validate Codes"}
            </button>
            <button
              onClick={runPreview}
              disabled={
                previewLoading ||
                importing ||
                validating ||
                importBlocked ||
                !validation ||
                !selectedBuilding
              }
              className="inline-flex items-center px-6 py-3 border border-primary-200 text-sm font-medium rounded-md shadow-sm text-primary-700 bg-white hover:bg-primary-50 disabled:opacity-50"
              title={
                !selectedBuilding
                  ? "Choose a building above to enable contact classification."
                  : undefined
              }
            >
              {previewLoading
                ? "Loading preview..."
                : contactPreview
                ? "Refresh Preview"
                : "Preview Contacts"}
            </button>
            <button
              onClick={handleImport}
              disabled={
                importing ||
                validating ||
                importBlocked ||
                !validation ||
                (Boolean(selectedBuilding) && !contactPreview) ||
                // First-import setup wizard gate (Bucket D3/D4): once
                // preview succeeded, the wizard must either be applied,
                // skipped, or show zero gaps before Confirm Import runs.
                (Boolean(selectedBuilding) &&
                  contactPreview !== null &&
                  !setupReady)
              }
              className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {importing ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Importing...
                </>
              ) : selectedBuilding && contactPreview ? (
                "Confirm Import"
              ) : (
                "Start Import"
              )}
            </button>
            {!importing && (
              <button
                onClick={handleReset}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            )}
          </div>

          {/* Contact Preview / Review — visible after runPreview() succeeds.
              Shows each contact classified against the target building with a
              per-row action selector so the treasurer can accept the
              auto-classification, match a contact to a specific unit
              (upgrading to a full exact-unit resident and also writing to
              Unit.residents[] on confirm), or ignore the contact (blocked
              when they have invoices in this import). */}
          {contactPreview && (
            <div className="mt-4 bg-white shadow overflow-hidden rounded-lg">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-900">
                  Contact preview ({contactPreview.contacts.length}) —{" "}
                  {contactPreview.building?.name ?? "no building"}
                </h3>
                <p className="text-xs text-gray-600 mt-1">
                  Review the auto-classification for each contact. Match a
                  contact to a specific unit to promote them to a full resident,
                  or ignore a contact you don&rsquo;t want imported. Contacts
                  with invoices in this import cannot be ignored.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2 text-left">Contact</th>
                      <th className="px-4 py-2 text-left">Address</th>
                      <th className="px-4 py-2 text-left">
                        Auto-classification
                      </th>
                      <th className="px-4 py-2 text-right">Invoices</th>
                      <th className="px-4 py-2 text-left">Action</th>
                      <th className="px-4 py-2 text-left">Unit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {contactPreview.contacts.map((c) => {
                      const o = overrides[c.key] ?? { action: "accept" };
                      const isIgnoreBlocked =
                        o.action === "ignore" && c.invoiceCount > 0;
                      const roleBadge = (() => {
                        switch (c.classified.role) {
                          case "resident":
                            return (
                              <span className="inline-flex items-center rounded-full bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 text-xs font-medium">
                                Resident
                                {c.classified.matchType === "building-partial"
                                  ? " (building)"
                                  : c.classified.unitId
                                  ? " (unit)"
                                  : ""}
                              </span>
                            );
                          case "supplier":
                            return (
                              <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-xs font-medium">
                                Supplier
                              </span>
                            );
                          default:
                            return (
                              <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 text-xs font-medium">
                                Customer
                              </span>
                            );
                        }
                      })();
                      return (
                        <tr
                          key={c.key}
                          className={isIgnoreBlocked ? "bg-red-50" : "bg-white"}
                        >
                          <td className="px-4 py-2 align-top">
                            <div className="font-medium text-gray-900">
                              {c.name}
                            </div>
                            {c.email && (
                              <div className="text-xs text-gray-500">
                                {c.email}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2 align-top text-gray-700 max-w-xs whitespace-normal">
                            {c.address || (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-2 align-top">
                            {roleBadge}
                            {c.classified.sharedTokens &&
                              c.classified.sharedTokens.length > 0 && (
                                <div className="text-xs text-gray-500 mt-1">
                                  match:{" "}
                                  {c.classified.sharedTokens
                                    .slice(0, 3)
                                    .join(", ")}
                                </div>
                              )}
                          </td>
                          <td className="px-4 py-2 align-top text-right text-gray-700">
                            {c.invoiceCount}
                          </td>
                          <td className="px-4 py-2 align-top">
                            <select
                              value={o.action}
                              onChange={(e) =>
                                setContactAction(
                                  c.key,
                                  e.target.value as OverrideAction,
                                  o.unitId,
                                )
                              }
                              className="rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                            >
                              <option value="accept">Accept</option>
                              <option value="assign-unit">Match to unit</option>
                              <option
                                value="ignore"
                                disabled={c.invoiceCount > 0}
                              >
                                Ignore
                                {c.invoiceCount > 0 ? " (blocked)" : ""}
                              </option>
                            </select>
                          </td>
                          <td className="px-4 py-2 align-top">
                            {o.action === "assign-unit" ? (
                              <select
                                value={o.unitId || ""}
                                onChange={(e) =>
                                  setContactAction(
                                    c.key,
                                    "assign-unit",
                                    e.target.value,
                                  )
                                }
                                className="rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                              >
                                <option value="">Pick a unit…</option>
                                {contactPreview.units.map((u) => (
                                  <option key={u.id} value={u.id}>
                                    {u.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* First-import setup wizard (Bucket D3/D4). Rendered inline below
              Preview Contacts. The "Analyse first-import setup" button
              triggers analyse mode on the server; the response's SetupPlan
              is presented as an editable checklist. Apply Setup runs a
              single atomic TransactWrite server-side. Confirm Import is
              gated until the wizard is either applied, skipped, or empty. */}
          {contactPreview && (
            <div className="mt-4 bg-white shadow overflow-hidden rounded-lg">
              <div className="px-4 py-3 bg-blue-50 border-b border-blue-200 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-blue-900">
                    First-import setup wizard
                  </h3>
                  <p className="text-xs text-blue-800 mt-1">
                    Derive management start date, service-charge plan, and
                    per-unit shares from the invoices in this import. Apply
                    these gaps before Confirm Import, or Skip if the building is
                    already set up.
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={runAnalyse}
                    disabled={analysing || applyingSetup || !selectedBuilding}
                    className="inline-flex items-center px-4 py-2 border border-blue-300 text-xs font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50"
                  >
                    {analysing
                      ? "Analysing…"
                      : setupPlan
                      ? "Re-analyse"
                      : "Analyse first-import setup"}
                  </button>
                  {!setupReady && (
                    <button
                      type="button"
                      onClick={skipSetup}
                      disabled={analysing || applyingSetup}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Skip setup
                    </button>
                  )}
                </div>
              </div>

              {analysing && (
                <div className="p-4 flex items-center gap-2 text-sm text-gray-600">
                  <Spinner /> Deriving setup gaps…
                </div>
              )}

              {setupPlan && setupPlan.gaps.length === 0 && (
                <div className="p-4 text-sm text-emerald-700 bg-emerald-50 border-t border-emerald-100">
                  ✓ Nothing to set up. The building already has a management
                  start date, an active service-charge plan, and configured
                  per-unit shares — proceed to Confirm Import.
                </div>
              )}

              {setupPlan && setupPlan.gaps.length > 0 && !applySetupResult && (
                <div className="p-4 space-y-4">
                  {setupPlan.sanityWarnings.length > 0 && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                      <p className="text-xs font-semibold text-amber-900">
                        Sanity checks
                      </p>
                      <ul className="mt-1 text-xs text-amber-800 space-y-1">
                        {setupPlan.sanityWarnings.map((w, idx) => (
                          <li key={idx}>• {w.message}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {setupPlan.gaps.map((gap) => {
                    if (gap.kind === "management-start-date") {
                      return (
                        <div
                          key="management-start-date"
                          className="rounded-md border border-gray-200 p-3"
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              id="gap-mgmt-start"
                              checked={!!acceptedGaps["management-start-date"]}
                              onChange={() =>
                                toggleAcceptGap("management-start-date")
                              }
                              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <label
                              htmlFor="gap-mgmt-start"
                              className="flex-1 text-sm"
                            >
                              <span className="font-medium text-gray-900">
                                Management start date
                              </span>
                              <span className="ml-2 text-xs text-gray-500">
                                (Building.managementStartDate)
                              </span>
                              <div className="mt-1 text-sm text-gray-800">
                                Set to{" "}
                                <span className="font-mono">
                                  {gap.suggestedDate}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Earliest invoice found in this import: ref{" "}
                                <span className="font-mono">
                                  {gap.evidence.sourceInvoiceRef}
                                </span>{" "}
                                dated{" "}
                                <span className="font-mono">
                                  {gap.evidence.sourceInvoiceDate}
                                </span>{" "}
                                from {gap.evidence.contactName}.
                              </div>
                            </label>
                          </div>
                        </div>
                      );
                    }
                    if (gap.kind === "service-charge-plan") {
                      return (
                        <div
                          key="service-charge-plan"
                          className="rounded-md border border-gray-200 p-3"
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              id="gap-plan"
                              checked={!!acceptedGaps["service-charge-plan"]}
                              onChange={() =>
                                toggleAcceptGap("service-charge-plan")
                              }
                              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <label
                              htmlFor="gap-plan"
                              className="flex-1 text-sm"
                            >
                              <span className="font-medium text-gray-900">
                                Service-charge plan
                              </span>
                              <span className="ml-2 text-xs text-gray-500">
                                (new ServiceChargePlan, status active)
                              </span>
                              <div className="mt-1 text-sm text-gray-800">
                                {formatPence(gap.suggestedMonthlyAmount)}{" "}
                                monthly from{" "}
                                <span className="font-mono">
                                  {gap.suggestedStartDate}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Derived from {gap.evidence.invoiceCount}{" "}
                                invoice(s) in {gap.evidence.monthLabel}:{" "}
                                {gap.evidence.perContact
                                  .map(
                                    (p) =>
                                      `${p.contactName} ${formatPence(
                                        p.amountPence,
                                      )}`,
                                  )
                                  .join(" · ")}
                              </div>
                              {gap.activePlansCount > 1 && (
                                <div className="text-xs text-red-600 mt-1">
                                  {gap.activePlansCount} active plans already
                                  exist. Applying this will create another —
                                  supersede duplicates from Building settings
                                  after import.
                                </div>
                              )}
                            </label>
                          </div>
                        </div>
                      );
                    }
                    if (gap.kind === "unit-shares") {
                      return (
                        <div
                          key="unit-shares"
                          className="rounded-md border border-gray-200 p-3"
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              id="gap-shares"
                              checked={!!acceptedGaps["unit-shares"]}
                              onChange={() => toggleAcceptGap("unit-shares")}
                              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <label
                              htmlFor="gap-shares"
                              className="flex-1 text-sm"
                            >
                              <span className="font-medium text-gray-900">
                                Per-unit service-charge shares
                              </span>
                              <span className="ml-2 text-xs text-gray-500">
                                (Unit.shareNumerator / shareDenominator)
                              </span>
                              <table className="mt-2 w-full text-xs">
                                <thead>
                                  <tr className="text-left text-gray-500">
                                    <th className="pr-3 py-1">Unit</th>
                                    <th className="pr-3 py-1">Resident</th>
                                    <th className="pr-3 py-1">Month one</th>
                                    <th className="pr-3 py-1">Share</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {gap.suggestions.map((s) => (
                                    <tr key={s.unitId}>
                                      <td className="pr-3 py-1 font-mono">
                                        {s.unitName}
                                      </td>
                                      <td className="pr-3 py-1">
                                        {s.contactName}
                                      </td>
                                      <td className="pr-3 py-1 font-mono">
                                        {formatPence(s.monthOneAmountPence)}
                                      </td>
                                      <td className="pr-3 py-1 font-mono">
                                        {s.numerator}/{s.denominator}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </label>
                          </div>
                        </div>
                      );
                    }
                    if (gap.kind === "retro-reclassify") {
                      return (
                        <div
                          key="retro-reclassify"
                          className="rounded-md border border-gray-200 bg-gray-50 p-3"
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              disabled
                              className="mt-1 h-4 w-4 rounded border-gray-300 opacity-50"
                            />
                            <div className="flex-1 text-sm">
                              <span className="font-medium text-gray-700">
                                Retro-reclassify {gap.candidates.length}{" "}
                                invoice(s) → service charges
                              </span>
                              <span className="ml-2 text-xs text-gray-500">
                                (deferred — Bucket D5)
                              </span>
                              <div className="text-xs text-gray-500 mt-1">
                                These invoices already persisted for contacts
                                now marked auto-service-charge. The reclassify
                                write ships next; today they stay as invoices
                                and can be reclassified manually from the
                                ContactDetails page.
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })}

                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={runApplySetup}
                      disabled={
                        applyingSetup ||
                        setupPlan.gaps.every(
                          (g) =>
                            !acceptedGaps[g.kind] ||
                            g.kind === "retro-reclassify",
                        )
                      }
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    >
                      {applyingSetup ? "Applying…" : "Apply setup"}
                    </button>
                  </div>
                </div>
              )}

              {applySetupResult && applySetupResult.applied && (
                <div className="p-4 text-sm text-emerald-800 bg-emerald-50 border-t border-emerald-100">
                  ✓ Setup applied.
                  {applySetupResult.applied.managementStartDate && (
                    <>
                      {" "}
                      Management start date ={" "}
                      <span className="font-mono">
                        {applySetupResult.applied.managementStartDate}
                      </span>
                      .
                    </>
                  )}
                  {applySetupResult.applied.planId && <> Plan created.</>}
                  {applySetupResult.applied.unitShareUpdates > 0 && (
                    <>
                      {" "}
                      {applySetupResult.applied.unitShareUpdates} unit share(s)
                      updated.
                    </>
                  )}
                  {applySetupResult.applied.retroReclassifyDeferred > 0 && (
                    <>
                      {" "}
                      {applySetupResult.applied.retroReclassifyDeferred}{" "}
                      retro-reclassify candidate(s) deferred to Bucket D5.
                    </>
                  )}
                </div>
              )}
              {/* Defensive rendering: when the server responded but the
                  `applied` payload is missing (e.g. HTTP 500 from the
                  Lambda whose error body is `{ message: "…" }` only),
                  show the error message rather than crashing on
                  `applied.managementStartDate` and unmounting the whole
                  tree — the earlier crash was the "blank screen after
                  Apply setup" reported on 2026-08-23. */}
              {applySetupResult && !applySetupResult.applied && (
                <div className="p-4 text-sm text-red-800 bg-red-50 border-t border-red-100">
                  Apply setup failed:{" "}
                  {applySetupResult.message ?? "unknown server error"}. The
                  building state is unchanged (the underlying TransactWrite is
                  atomic — either every gap landed or none did). You can
                  Re-analyse and try again, or Skip setup and proceed.
                </div>
              )}

              {setupSkipped && !applySetupResult && (
                <div className="p-4 text-sm text-gray-700 bg-gray-50 border-t border-gray-100">
                  Setup wizard skipped — proceeding without applying gaps. You
                  can still edit management start date, plan and shares from
                  Building settings afterwards.
                </div>
              )}
            </div>
          )}

          {validation && (
            <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-gray-900">
                Validation
              </h3>
              <p className="mt-1 text-xs text-gray-600">
                {validation.readyToImport
                  ? "All nominal codes are mapped. Import is ready."
                  : "Import blocked. Resolve all unknown nominal codes below."}
              </p>
              {validation.validationErrors.length > 0 && (
                <ul className="mt-2 text-xs text-red-700 space-y-1">
                  {validation.validationErrors.map((msg, idx) => (
                    <li key={idx}>• {msg}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {unresolvedCodes.length > 0 && (
            <div className="mt-4 bg-white shadow rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-amber-50 border-b border-amber-200">
                <h3 className="text-sm font-medium text-amber-900">
                  Unknown nominal codes ({unresolvedCodes.length})
                </h3>
                <p className="text-xs text-amber-800 mt-1">
                  Create categories for each code to continue with import.
                </p>
              </div>
              <div className="divide-y divide-gray-200">
                {unresolvedCodes.map((codeInfo) => {
                  const draft =
                    resolutionDrafts[codeInfo.internalCode] ||
                    buildDefaultDraft(codeInfo);
                  return (
                    <div key={codeInfo.internalCode} className="p-4">
                      <div className="flex flex-wrap gap-3 items-center">
                        <span className="font-mono text-sm text-gray-700">
                          {codeInfo.code} &rarr; {codeInfo.internalCode}
                        </span>
                        <span className="text-xs text-gray-500">
                          Sheets: {codeInfo.sheetNames.join(", ")}
                        </span>
                        <span className="text-xs text-gray-500">
                          Occurrences: {codeInfo.occurrences}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-gray-600">
                        Sample: [{codeInfo.sample.sheetName}]{" "}
                        {codeInfo.sample.date || "-"} |{" "}
                        {codeInfo.sample.description || "-"} |{" "}
                        {codeInfo.sample.amount !== undefined
                          ? codeInfo.sample.amount.toFixed(2)
                          : "-"}{" "}
                        | raw code {codeInfo.sample.rawCode}
                      </div>
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-5 gap-2">
                        <input
                          value={draft.name}
                          onChange={(e) =>
                            handleDraftChange(
                              codeInfo.internalCode,
                              "name",
                              e.target.value,
                            )
                          }
                          placeholder="Category name"
                          className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                        />
                        <select
                          value={draft.categoryType}
                          onChange={(e) =>
                            handleDraftChange(
                              codeInfo.internalCode,
                              "categoryType",
                              e.target.value,
                            )
                          }
                          className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                        >
                          <option value="income">Income</option>
                          <option value="expense">Expense</option>
                          <option value="asset">Asset</option>
                          <option value="liability">Liability</option>
                          <option value="equity">Equity</option>
                        </select>
                        <select
                          value={draft.statementSection}
                          onChange={(e) =>
                            handleDraftChange(
                              codeInfo.internalCode,
                              "statementSection",
                              e.target.value,
                            )
                          }
                          className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                        >
                          <option value="profit-loss">Profit &amp; Loss</option>
                          <option value="balance-sheet">Balance Sheet</option>
                        </select>
                        <select
                          value={draft.parentCode || ""}
                          onChange={(e) =>
                            handleDraftChange(
                              codeInfo.internalCode,
                              "parentCode",
                              e.target.value,
                            )
                          }
                          className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                        >
                          <option value="">Select parent group</option>
                          {parentGroups.map((group) => (
                            <option
                              key={group.nominalCode}
                              value={group.nominalCode}
                            >
                              {group.nominalCode} {group.name}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleResolveCode(codeInfo)}
                          disabled={resolvingCode === codeInfo.internalCode}
                          className="inline-flex items-center justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                        >
                          {resolvingCode === codeInfo.internalCode
                            ? "Saving..."
                            : "Create Category"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Error */}
      {error && (
        <div className="mt-4">
          <ErrorAlert message={error} />
        </div>
      )}

      {/* Import result */}
      {result && (
        <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
          <div
            className={`px-4 py-3 border-b ${
              result.summary.errors.length > 0
                ? "bg-amber-50 border-amber-200"
                : "bg-green-50 border-green-200"
            }`}
          >
            <h3
              className={`text-sm font-medium ${
                result.summary.errors.length > 0
                  ? "text-amber-800"
                  : "text-green-800"
              }`}
            >
              {result.message}
            </h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                {
                  label: "Categories",
                  count: result.summary.accountingCategories,
                },
                { label: "Bank Accounts", count: result.summary.bankAccounts },
                { label: "Contacts", count: result.summary.contacts },
                {
                  label: "Financial Records",
                  count: result.summary.financialRecords,
                },
                { label: "Invoices", count: result.summary.invoicesImported },
                { label: "Bills", count: result.summary.billsImported },
                {
                  label: "Transactions",
                  count: result.summary.bankTransactions,
                },
                {
                  label: "Journal Entries",
                  count: result.summary.journalEntries,
                },
              ].map(({ label, count }) => (
                <div
                  key={label}
                  className="text-center p-3 bg-gray-50 rounded-lg"
                >
                  <p className="text-2xl font-semibold text-gray-900">
                    {count}
                  </p>
                  <p className="text-xs text-gray-500">{label}</p>
                </div>
              ))}
            </div>

            {result.summary.errors.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-red-700 mb-2">
                  Errors ({result.summary.errors.length})
                </h4>
                <ul className="text-sm text-red-600 space-y-1">
                  {result.summary.errors.map((err, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <span className="text-red-400 mt-0.5">•</span>
                      {err}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4">
              <button
                onClick={handleReset}
                className="text-sm text-primary-600 hover:text-primary-800"
              >
                Import another file
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {importing && (
        <div className="mt-6">
          <Spinner message="Importing FreeAgent data... This may take a moment." />
        </div>
      )}
    </div>
  );
};

export default FreeAgentImport;
